/**
 * MNPI restricted/watch list + wall-crossing log data access (migration 0011:
 * restricted_list_entries + wall_crossing_events). Client-side under RLS.
 *
 * Removal is a status flip + removal metadata, never a DELETE — the history is
 * the exam artifact (build doc §12 / §17.6 audit-trail-is-sacred). The wall log
 * is append-only at the DB level (update/delete revoked from `authenticated`).
 */
import { supabase } from "@/lib/supabase/client";

/* ------------------------------------------------ Restricted list entries */

export type ListType = "restricted" | "watch";
export type EntryStatus = "active" | "removed";
export type EntrySource =
  | "board_seat"
  | "deal_diligence"
  | "nda"
  | "advisory_role"
  | "extraction_flag"
  | "manual"
  | "other";

export interface RestrictedEntryRow {
  id: string;
  tenant_id: string;
  issuer_name: string;
  ticker: string | null;
  list_type: ListType;
  reason: string;
  source: EntrySource;
  fund_id: string | null;
  status: EntryStatus;
  effective_at: string;
  removed_at: string | null;
  removed_by: string | null;
  removal_reason: string | null;
  notes: string | null;
  added_by: string | null;
  created_at: string;
}

const ENTRY_COLS =
  "id, tenant_id, issuer_name, ticker, list_type, reason, source, fund_id, status, effective_at, removed_at, removed_by, removal_reason, notes, added_by, created_at";

export async function listRestrictedEntries(tenantId: string): Promise<RestrictedEntryRow[]> {
  const { data, error } = await supabase
    .from("restricted_list_entries")
    .select(ENTRY_COLS)
    .eq("tenant_id", tenantId)
    .order("status", { ascending: true })
    .order("effective_at", { ascending: false });
  if (error) throw new Error(`listRestrictedEntries: ${error.message}`);
  return data ?? [];
}

export interface AddRestrictedEntryInput {
  tenant_id: string;
  issuer_name: string;
  ticker?: string | null;
  list_type: ListType;
  reason: string;
  source: EntrySource;
  fund_id?: string | null;
  notes?: string | null;
  added_by?: string | null;
}

export async function addRestrictedEntry(input: AddRestrictedEntryInput): Promise<void> {
  const { error } = await supabase.from("restricted_list_entries").insert(input);
  if (error) throw new Error(`addRestrictedEntry: ${error.message}`);
}

/**
 * Remove an entry from the active list. Never deletes the row — flips
 * `status` to 'removed' and stamps removal metadata so the history (why it
 * was added, why it was removed) survives for the quarterly review + exam.
 */
export async function removeRestrictedEntry(
  id: string,
  removalReason: string,
  userId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("restricted_list_entries")
    .update({
      status: "removed",
      removed_at: new Date().toISOString(),
      removed_by: userId,
      removal_reason: removalReason,
    })
    .eq("id", id);
  if (error) throw new Error(`removeRestrictedEntry: ${error.message}`);
}

/* --------------------------------------------------- Wall-crossing events */

export type WallEventType =
  | "wall_crossing_granted"
  | "wall_crossing_denied"
  | "access_review"
  | "list_screen";

export interface WallEventRow {
  id: string;
  tenant_id: string;
  restricted_entry_id: string | null;
  person_name: string;
  person_email: string | null;
  person_role: string | null;
  event_type: WallEventType;
  matter: string | null;
  decision_basis: string | null;
  approved_by: string | null;
  occurred_at: string;
  recorded_by: string | null;
  notes: string | null;
}

const WALL_EVENT_COLS =
  "id, tenant_id, restricted_entry_id, person_name, person_email, person_role, event_type, matter, decision_basis, approved_by, occurred_at, recorded_by, notes";

export async function listWallEvents(tenantId: string): Promise<WallEventRow[]> {
  const { data, error } = await supabase
    .from("wall_crossing_events")
    .select(WALL_EVENT_COLS)
    .eq("tenant_id", tenantId)
    .order("occurred_at", { ascending: false });
  if (error) throw new Error(`listWallEvents: ${error.message}`);
  return data ?? [];
}

export interface RecordWallEventInput {
  tenant_id: string;
  restricted_entry_id?: string | null;
  person_name: string;
  person_email?: string | null;
  person_role?: string | null;
  event_type: WallEventType;
  matter?: string | null;
  decision_basis?: string | null;
  approved_by?: string | null;
  occurred_at?: string;
  notes?: string | null;
  /** Set by the caller from the authenticated session — RLS also enforces
   * this equals auth.uid() (or is null) at the DB level. */
  recorded_by?: string | null;
}

export async function recordWallEvent(input: RecordWallEventInput): Promise<void> {
  const { error } = await supabase.from("wall_crossing_events").insert(input);
  if (error) throw new Error(`recordWallEvent: ${error.message}`);
}
